import { useState } from 'react';

import { config } from '~app/config';

import type { OrderTableData } from '~widgets/orders-table/model/orders-table-data';

import CheckCircleIcon from '~shared/assets/icons/check-circle.svg?react';
import { MoneyPh } from '~shared/ui/money-ph';

import { Button } from '@breezeventures/ui-kit/button';
import { Typography } from '@breezeventures/ui-kit/typography';
import { format } from 'date-fns';

import { OrderStatusBadge } from '../order-status-badge';

const EMPTY_VALUE = '—';

type DetailRowProps = {
  label: string;
  children: React.ReactNode;
};

const DetailRow = ({ label, children }: DetailRowProps) => (
  <li className="grid grid-cols-2 items-start gap-4 px-4 py-4">
    <Typography.Text size="m" color="secondary" tag="span">
      {label}
    </Typography.Text>
    <Typography.Text size="m" tag="span" className="min-w-0 text-left">
      {children}
    </Typography.Text>
  </li>
);

export const OrderDetails = ({ order }: { order: OrderTableData }) => {
  const dateObj = new Date(order.orderTimestampUTC);
  const link = `${config.onlineHost}/order/${order.orderId}`;
  const [isCopied, setIsCopied] = useState(false);

  const handleCopyClick = async () => {
    try {
      await navigator.clipboard.writeText(link);
      setIsCopied(true);
    } catch {
      // clipboard may be unavailable or blocked by the browser
    }
  };

  const handleCopyBlur = () => {
    if (isCopied) {
      setTimeout(() => setIsCopied(false), 200);
    }
  };

  return (
    <div className="flex flex-col gap-4 pb-2">
      <ul
        className={`
          m-0 list-none divide-y divide-separator overflow-hidden rounded-xl
          border border-separator p-0
        `}
      >
        <DetailRow label="Date">
          <span className="flex flex-col items-start gap-0.5">
            <span>{format(dateObj, 'dd.MM.yyyy')}</span>
            <Typography.Text size="xs" color="secondary" tag="span">
              {format(dateObj, 'hh:mm a')}
            </Typography.Text>
          </span>
        </DetailRow>

        <DetailRow label="Order number">
          {order.shopOrderId ? (
            <span className="wrap-break-word">{order.shopOrderId}</span>
          ) : (
            EMPTY_VALUE
          )}
        </DetailRow>

        <DetailRow label="Internal order number">{order.orderNumber}</DetailRow>

        <DetailRow label="Customer name">
          {order.customerFullName || EMPTY_VALUE}
        </DetailRow>

        <DetailRow label="Business">
          {order.merchantName || EMPTY_VALUE}
        </DetailRow>

        <DetailRow label="Store">{order.posName || EMPTY_VALUE}</DetailRow>

        <DetailRow label="Status">
          <OrderStatusBadge status={order.orderStatus} />
        </DetailRow>

        <DetailRow label="Number of items">{order.itemsCount}</DetailRow>

        <DetailRow label="Total">
          <MoneyPh value={order.totalAmount} />
        </DetailRow>
      </ul>

      <div className="flex justify-center pt-2">
        {isCopied ? (
          <div
            onMouseOut={handleCopyBlur}
            onBlur={handleCopyBlur}
            className={`
              flex h-[44px] items-center gap-2 px-4 font-body text-sm
              text-positive-accent
            `}
          >
            <CheckCircleIcon className="size-6" /> Copied
          </div>
        ) : (
          <Button size="l" width="full" onClick={handleCopyClick}>
            Copy order link
          </Button>
        )}
      </div>
    </div>
  );
};
